import React, { useEffect, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Skeleton,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  Grid,
  Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import StarIcon from "@mui/icons-material/Star";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Swal from "sweetalert2";
import { api, API_BASE } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import AddClientForm from "./AddClientForm";
import ClientFilterDrawer, { emptyFilters } from "./ClientFilterDrawer";

const gradeColor = (g) => {
  switch ((g || "").toUpperCase()) {
    case "A+": return "success";
    case "A":  return "primary";
    case "B":  return "info";
    case "C":  return "warning";
    case "D":  return "error";
    default:   return "default";
  }
};

// Convert filter object → query string for API
const toQuery = (page, pageSize, f) => {
  const q = new URLSearchParams({
    page,
    pageSize,
    search: f.search || "",
    gst: f.gst || "",
    pan: f.pan || "",
    mobile: f.mobile || "",
    email: f.email || "",
    pincode: f.pincode || "",
    grade: (f.grade || []).join(","),
    business: (f.business || []).join(","),
    state: (f.state || []).join(","),
    city: (f.city || []).join(","),
    has_gst: f.has_gst || "",
    has_email: f.has_email || "",
    has_mobile: f.has_mobile || "",
    created_from: f.created_from || "",
    created_to: f.created_to || "",
    sort_by: f.sort_by || "id",
    sort_order: f.sort_order || "desc",
  });
  return q.toString();
};

// Count active filters (for badge)
const countActive = (f) => {
  let c = 0;
  if (f.search) c++;
  if (f.gst) c++;
  if (f.pan) c++;
  if (f.mobile) c++;
  if (f.email) c++;
  if (f.pincode) c++;
  if (f.grade?.length) c++;
  if (f.business?.length) c++;
  if (f.state?.length) c++;
  if (f.city?.length) c++;
  if (f.has_gst) c++;
  if (f.has_email) c++;
  if (f.has_mobile) c++;
  if (f.created_from) c++;
  if (f.created_to) c++;
  return c;
};

// Build chips of active filters
const activeChips = (f, onRemove) => {
  const chips = [];
  const add = (key, label, value) =>
    chips.push({ key, label: `${label}: ${value}` });

  if (f.search) add("search", "Name", f.search);
  if (f.gst) add("gst", "GSTIN", f.gst);
  if (f.pan) add("pan", "PAN", f.pan);
  if (f.mobile) add("mobile", "Mobile", f.mobile);
  if (f.email) add("email", "Email", f.email);
  if (f.pincode) add("pincode", "Pincode", f.pincode);
  if (f.grade?.length) add("grade", "Grade", f.grade.join(", "));
  if (f.business?.length) add("business", "Type", f.business.join(", "));
  if (f.state?.length) add("state", "State", f.state.join(", "));
  if (f.city?.length) add("city", "City", f.city.join(", "));
  if (f.has_gst) add("has_gst", "Has GST", f.has_gst);
  if (f.has_email) add("has_email", "Has Email", f.has_email);
  if (f.has_mobile) add("has_mobile", "Has Mobile", f.has_mobile);
  if (f.created_from) add("created_from", "From", f.created_from);
  if (f.created_to) add("created_to", "To", f.created_to);

  return chips.map((c) => (
    <Chip
      key={c.key}
      label={c.label}
      size="small"
      onDelete={() => onRemove(c.key)}
    />
  ));
};

const StatCard = ({ label, value, icon, color, loading }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ bgcolor: `${color}.main`, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={50} height={28} />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {value ?? 0}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default function Clients() {
  const { can } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [quickSearch, setQuickSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);

  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  const loadClients = useCallback(async (pageNo, pageSize, f) => {
    try {
      setLoading(true);
      const res = await api.get(`/clients/list.php?${toQuery(pageNo, pageSize, f)}`);
      setClients(res.clients || []);
      setPagination(
        res.pagination || { page: pageNo, pageSize, totalPages: 0, totalRecords: 0 }
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await api.get("/clients/stats.php");
      setStats(res.stats || {});
    } catch (e) {
      console.error(e);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients(1, 10, emptyFilters);
    loadStats();
  }, [loadClients, loadStats]);

  const handleQuickSearch = () => {
    const next = { ...filters, search: quickSearch };
    setFilters(next);
    loadClients(1, pagination.pageSize, next);
  };

  const handleApplyFilters = (next) => {
    setFilters(next);
    setQuickSearch(next.search || "");
    loadClients(1, pagination.pageSize, next);
  };

  const handleRemoveChip = (key) => {
    const next = { ...filters };
    if (Array.isArray(next[key])) next[key] = [];
    else next[key] = "";
    if (key === "search") setQuickSearch("");
    setFilters(next);
    loadClients(1, pagination.pageSize, next);
  };

  const handleClearAll = () => {
    setFilters(emptyFilters);
    setQuickSearch("");
    loadClients(1, pagination.pageSize, emptyFilters);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${API_BASE}/clients/export.php?${toQuery(1, 1, filters)}`;
      const res = await fetch(url, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `clients_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Export Failed", text: err.message });
    }
  };

  const handleDelete = async (client) => {
    const result = await Swal.fire({
      title: "Delete Client?",
      text: `"${client.business_name}" will be deleted permanently.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      await api.get(`/clients/delete.php?id=${client.id}`);
      Swal.fire({ icon: "success", title: "Deleted", timer: 1200, showConfirmButton: false });
      loadClients(pagination.page, pagination.pageSize, filters);
      loadStats();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to delete client" });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_e, newPage) => {
    loadClients(newPage + 1, pagination.pageSize, filters);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    loadClients(1, newSize, filters);
  };

  const activeCount = countActive(filters);
  const chips = activeChips(filters, handleRemoveChip);

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <PeopleIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Clients
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your clients, details & service assignments
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          {can("clients.edit") && (
            <Button
              component={RouterLink}
              to="/client/assign-bulk-services"
              variant="outlined"
              startIcon={<BoltIcon />}
            >
              Bulk Assign
            </Button>
          )}
          {can("clients.view") && (
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
          {can("clients.create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Client
            </Button>
          )}
        </Stack>
      </Stack>

      {/* STATS STRIP */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Clients"
            value={stats.total}
            icon={<PeopleIcon />}
            color="primary"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="A+ Grade"
            value={stats.a_plus}
            icon={<StarIcon />}
            color="success"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="New This Month"
            value={stats.new_this_month}
            icon={<NewReleasesIcon />}
            color="info"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Missing GSTIN"
            value={stats.missing_gst}
            icon={<WarningAmberIcon />}
            color="warning"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* SEARCH + FILTER BAR */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Quick search by name / contact person"
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleQuickSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <Button
            variant="contained"
            onClick={handleQuickSearch}
            startIcon={<SearchIcon />}
          >
            Search
          </Button>
          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterAltIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>
        </Stack>

        {/* ACTIVE FILTER CHIPS */}
        {chips.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}
            alignItems="center"
          >
            {chips}
            <Button size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          </Stack>
        )}
      </Paper>

      {/* TABLE */}
      <Paper>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>#</TableCell>
                <TableCell>Client Details</TableCell>
                <TableCell sx={{ width: 140 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                [...Array(5)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={3}>
                      <Skeleton height={48} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && clients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                    <Typography variant="h3" sx={{ mb: 1 }}>📁</Typography>
                    <Typography variant="body2" color="text.secondary">
                      No clients found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                clients.map((c, idx) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      {(pagination.page - 1) * pagination.pageSize + idx + 1}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {c.grade && (
                          <Chip
                            label={c.grade}
                            color={gradeColor(c.grade)}
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 36 }}
                          />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                            <Typography
                              component={RouterLink}
                              to={`/clients/${c.id}`}
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "text.primary",
                                textDecoration: "none",
                                "&:hover": { color: "primary.main" },
                              }}
                            >
                              {c.business_name}
                            </Typography>
                            {c.business_type && (
                              <Chip
                                label={c.business_type}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 10 }}
                              />
                            )}
                          </Stack>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {c.contact_person && <>{c.contact_person} · </>}
                            📞 {c.mobile}
                            {c.email && <> · ✉️ {c.email}</>}
                          </Typography>

                          <Typography variant="caption" color="text.secondary" display="block">
                            <b>PAN:</b> {c.pan || "N/A"} &nbsp;|&nbsp; <b>GST:</b> {c.gstin || "N/A"}
                          </Typography>

                          {c.address && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              display="block"
                              sx={{ display: { xs: "none", md: "block" } }}
                            >
                              {c.address}, {c.city}, {c.state} - {c.pincode}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View">
                        <IconButton
                          component={RouterLink}
                          to={`/clients/${c.id}`}
                          size="small"
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {can("clients.delete") && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(c)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>

        <Divider />

        <TablePagination
          component="div"
          count={pagination.totalRecords || 0}
          page={Math.max(0, (pagination.page || 1) - 1)}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.pageSize || 10}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* FILTER DRAWER */}
      <ClientFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      {/* ADD CLIENT DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1, fontWeight: 700 }}>Add Client</Box>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AddClientForm
            onSuccess={() => {
              setOpen(false);
              loadClients(pagination.page, pagination.pageSize, filters);
              loadStats();
            }}
            onClose={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
